import type {
    ConfigOwner,
    ConfigTarget,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import { create } from "zustand";
import { getPlatformService } from "../platform";

export type SettingsSection = "editor" | "runtime" | "plugin" | "sidecar";

interface CommitParams {
    owner: ConfigOwner;
    target: ConfigTarget;
    dotPath?: string;
    value?: unknown;
    patch?: Record<string, unknown>;
    unset?: string[];
}

interface SettingsStoreState {
    searchQuery: string;
    activeSection: SettingsSection;
    activeTarget: ConfigTarget;
    schema: SettingsRegistryEntry[];
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    staleKeys: string[];
    loadingSchema: boolean;
    loadingSnapshot: boolean;
    savingKeys: string[];
    error: string | null;

    loadSchema: () => Promise<void>;
    loadSnapshot: () => Promise<void>;
    ensureLoaded: () => Promise<void>;
    commitValue: (params: CommitParams) => Promise<void>;
    clearValue: (
        owner: ConfigOwner,
        target: ConfigTarget,
        keys: string[],
    ) => Promise<void>;
    setDraft: (dotPath: string, value: unknown) => void;
    discardDraft: (dotPath: string) => void;
    markStale: (keys: string[]) => void;
    clearStale: (keys?: string[]) => void;
    handleConfigChanged: (keys: string[]) => Promise<void>;
    setSearchQuery: (query: string) => void;
    setActiveSection: (section: SettingsSection) => void;
    setActiveTarget: (target: ConfigTarget) => void;
    reset: () => void;
}

export const useSettingsStore = create<SettingsStoreState>((set, get) => ({
    searchQuery: "",
    activeSection: "editor",
    activeTarget: "user",
    schema: [],
    snapshot: null,
    drafts: {},
    staleKeys: [],
    loadingSchema: false,
    loadingSnapshot: false,
    savingKeys: [],
    error: null,

    async loadSchema() {
        set({ loadingSchema: true, error: null });
        try {
            const schema = await getPlatformService().settings.getSchema();
            set({ schema: schema.entries, loadingSchema: false });
        } catch (error) {
            set({
                loadingSchema: false,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    },

    async loadSnapshot() {
        set({ loadingSnapshot: true, error: null });
        try {
            const snapshot = await getPlatformService().settings.getSnapshot();
            set((state) => ({
                snapshot,
                loadingSnapshot: false,
                staleKeys: state.staleKeys.filter((key) => key in state.drafts),
            }));
        } catch (error) {
            set({
                loadingSnapshot: false,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    },

    async ensureLoaded() {
        const state = get();
        if (state.schema.length > 0 && state.snapshot) return;
        const tasks: Promise<void>[] = [];
        if (!state.loadingSchema) {
            tasks.push(get().loadSchema());
        }
        if (!state.loadingSnapshot) {
            tasks.push(get().loadSnapshot());
        }
        if (tasks.length > 0) {
            await Promise.all(tasks);
        }
    },

    async commitValue(params) {
        const affectedKeys = [
            ...(params.dotPath ? [params.dotPath] : []),
            ...Object.keys(params.patch ?? {}),
            ...(params.unset ?? []),
        ];
        set((state) => ({
            savingKeys: dedupeStrings([...state.savingKeys, ...affectedKeys]),
            error: null,
        }));
        try {
            await getPlatformService().settings.setValue(params);
            set((state) => {
                const nextDrafts = { ...state.drafts };
                for (const key of affectedKeys) {
                    delete nextDrafts[key];
                }
                return {
                    drafts: nextDrafts,
                    staleKeys: state.staleKeys.filter(
                        (key) => !affectedKeys.includes(key),
                    ),
                };
            });
            await get().loadSnapshot();
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : String(error),
            });
        } finally {
            set((state) => ({
                savingKeys: state.savingKeys.filter(
                    (key) => !affectedKeys.includes(key),
                ),
            }));
        }
    },

    async clearValue(owner, target, keys) {
        await get().commitValue({
            owner,
            target,
            unset: keys,
        });
    },

    setDraft(dotPath, value) {
        set((state) => ({
            drafts: {
                ...state.drafts,
                [dotPath]: value,
            },
        }));
    },

    discardDraft(dotPath) {
        set((state) => {
            const nextDrafts = { ...state.drafts };
            delete nextDrafts[dotPath];
            return {
                drafts: nextDrafts,
                staleKeys: state.staleKeys.filter((key) => key !== dotPath),
            };
        });
    },

    markStale(keys) {
        set((state) => ({
            staleKeys: dedupeStrings([...state.staleKeys, ...keys]),
        }));
    },

    clearStale(keys) {
        if (!keys) {
            set({ staleKeys: [] });
            return;
        }
        set((state) => ({
            staleKeys: state.staleKeys.filter((key) => !keys.includes(key)),
        }));
    },

    async handleConfigChanged(keys) {
        if (keys.length === 0) return;
        const draftKeys = Object.keys(get().drafts);
        const conflictedKeys = keys.filter((key) => draftKeys.includes(key));
        const passiveKeys = keys.filter((key) => !draftKeys.includes(key));

        if (conflictedKeys.length > 0) {
            get().markStale(conflictedKeys);
        }
        if (passiveKeys.length > 0) {
            await get().loadSnapshot();
        }
    },

    setSearchQuery(searchQuery) {
        set({ searchQuery });
    },

    setActiveSection(activeSection) {
        set({ activeSection });
    },

    setActiveTarget(activeTarget) {
        set({ activeTarget });
    },

    reset() {
        set({
            searchQuery: "",
            activeSection: "editor",
            activeTarget: "user",
            schema: [],
            snapshot: null,
            drafts: {},
            staleKeys: [],
            loadingSchema: false,
            loadingSnapshot: false,
            savingKeys: [],
            error: null,
        });
    },
}));

function dedupeStrings(values: string[]): string[] {
    return [...new Set(values)];
}
